import { STATUS_BAR_DEFAULT_STYLE } from "@/constants/statusBar";
import { StatusBarStyleEnum } from "@/enums/global";
import { getBootFailureCode } from "@/methods/api";
import { isInternetAvailable, handleAppUpdates } from "@/methods/appUtils";
import { blurActiveElement } from "@/methods/focus";
import { getFailedModuleUrl, isDynamicImportError } from "@/methods/moduleLoading";
import { allRoutes } from "@/routes/allRoutes";
import { setAppRouter } from "@/routerInstance";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";
import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "@ionic/vue-router";

const missingRoute = [{ path: "/:pathMatch(.*)*", redirect: "/" }];

const routes = allRoutes.concat(missingRoute as Array<RouteRecordRaw>);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes as Array<RouteRecordRaw>,
  scrollBehavior(to, _, savedPosition) {
    const activePage = document.querySelector(
      "ion-router-outlet > .ion-page.ion-page-invisible-not, ion-router-outlet > .ion-page",
    );
    const activeIonContent = activePage?.querySelector(
      "ion-content",
    ) as HTMLIonContentElement | null;
    if (activeIonContent) {
      if (savedPosition && !to.meta.scrollToTop) {
        void activeIonContent.scrollToPoint(savedPosition.left ?? 0, savedPosition.top, 300);
      } else {
        void activeIonContent.scrollToTop(300);
      }
      return false;
    }
    if (savedPosition && !to.meta.scrollToTop) {
      return savedPosition;
    } else {
      return { top: 0, behavior: "smooth" };
    }
  },
});

// Register the instance for store/utility consumers (see src/routerInstance.ts —
// importing "@/router" from those modules creates a circular module graph).
setAppRouter(router);

function updateLoaderText(text: string) {
  const loaderText = document.getElementById("app-loader-text");
  if (loaderText) loaderText.textContent = text;
}

// App bootstrap is a once-per-page-load concern, so it is tracked here rather than inferred from
// `from.name === undefined`. Router bookkeeping cannot carry that meaning: a redirect chain, or a
// navigation cancelled by a concurrent router.replace, leaves `from` at START, so keying off it
// re-ran the whole bootstrap on every re-entry — 78 times in 11s during the 2026-08-06 staging
// cascade, each pass firing the authenticated calls that produced the next round of failures.
// Concurrent entrants await the same promise instead of starting a second bootstrap; a thrown
// bootstrap clears it so a genuinely transient failure can still be retried by a later
// navigation.
let appBootstrapPromise: Promise<boolean> | null = null;

function bootstrapApp(): Promise<boolean> {
  if (appBootstrapPromise === null) {
    appBootstrapPromise = (async () => {
      updateLoaderText("Loading configuration...");
      await useAppSettingsStore().initializeAppConfig();
      updateLoaderText("Preparing your dashboard...");
      return await useSessionStore().validateLoggedInSession();
    })().catch((error) => {
      appBootstrapPromise = null;
      throw error;
    });
  }
  return appBootstrapPromise;
}

router.beforeEach(async (to, from) => {
  // Drop focus from the control that triggered this navigation before Ionic marks the outgoing
  // page `ion-page-hidden` + `aria-hidden` for the transition — otherwise the still-focused button
  // trips Chrome's "Blocked aria-hidden ... retained focus" warning. See @/methods/focus.
  blurActiveElement();
  const sessionStore = useSessionStore();
  const { LastPingStatusCode, LastMaintenanceReason } = storeToRefs(sessionStore);
  const hasInternet = await isInternetAvailable();
  const allowOffline = to.meta.allowOffline as boolean;
  if (to.name === "CheckInternetConnection" || (allowOffline && !hasInternet)) {
    await sessionStore.afterNavigateValidated();
    return true;
  }
  if (to.name === "UnderMaintenance" || to.name === "TooManyRequests") {
    if (from.name === undefined) {
      updateLoaderText("Loading your dashboard...");
      await sessionStore.beforeNavigateStart();
      // A healthy ping alone used to be enough to bounce back to the app, which made a bootstrap
      // failure unreachable: bootstrap only runs once the ping has succeeded, so the redirect it
      // returned arrived here, saw a healthy ping, and sent the user straight back to the route
      // that had just failed — a redirect loop that vue-router aborts, leaving the user on a dead
      // page rather than on this one. A recorded reason means the failure is still live.
      if (LastPingStatusCode.value === HttpStatusCode.Ok && LastMaintenanceReason.value === null) {
        return { name: "LandingPage" };
      }
    }
    await sessionStore.afterNavigateValidated();
    return true;
  }
  updateLoaderText("Connecting to server...");
  if (!hasInternet) {
    return { name: "CheckInternetConnection" };
  }
  await sessionStore.beforeNavigateStart();
  if (LastPingStatusCode.value !== HttpStatusCode.Ok) {
    if (LastPingStatusCode.value === HttpStatusCode.TooManyRequests) {
      return { name: "TooManyRequests" };
    }
    return { name: "UnderMaintenance" };
  }
  const appSettingsStore = useAppSettingsStore();
  if (to.name !== "CheckInternetConnection" && to.name !== "Logout") {
    try {
      const isValidSession = await bootstrapApp();
      if (!isValidSession) {
        // A rejected session is a fact about this navigation, not about the page load.
        // validateLoggedInSession() has already started a logout, and the login that follows it
        // must be able to navigate — caching this false would veto every subsequent navigation
        // until a full reload, leaving the user stranded on the login screen after signing in.
        appBootstrapPromise = null;
        sessionStore.hideAppLoader();
        return false;
      }
      // The only event that disproves a recorded bootstrap failure, and so the only one allowed
      // to retire it — a healthy ping cannot, because the ping succeeds before bootstrap runs.
      sessionStore.setMaintenanceReason(null);
    } catch (error) {
      // This catch is the widest net in the app: it covers the API calls bootstrapApp makes AND
      // any exception thrown while running them. Swallowing the error made every one of those
      // indistinguishable on the failure screen, so log it whole and keep a short code for the
      // user to read back.
      console.error("App bootstrap failed:", error);
      sessionStore.setMaintenanceReason(getBootFailureCode(error));
      sessionStore.hideAppLoader();
      return { name: "UnderMaintenance" };
    }
  }
  let isValidNavigation: boolean;
  const { IsLoggedIn, UserInfo } = storeToRefs(sessionStore);
  if (to.meta.requiresAuth) {
    if (IsLoggedIn.value) {
      const allowedRoleIds = (to.meta.allowedRoleIds as Array<number>) || [];
      const userRoleId = UserInfo.value.RoleId;
      if (allowedRoleIds.includes(userRoleId)) {
        isValidNavigation = true;
      } else {
        return { name: "Unauthorized" };
      }
    } else {
      // BR-UI-100: carry where the user was trying to go through the login round-trip. The highest
      // volume entry point in the QR feature is /qr-code/:qrCode — the deep link printed inside
      // physical workbooks — and a signed-out student who lands there is stranded on the dashboard
      // unless the target survives. Only the path is kept, never a full URL, so this cannot be used
      // to bounce a user off-site after login.
      return { name: "LandingPage", query: { returnUrl: to.fullPath } };
    }
  } else if (to.meta.requiresUnAuth && IsLoggedIn.value && appSettingsStore.AppMenu) {
    return { name: appSettingsStore.AppMenu.DefaultView };
  } else {
    isValidNavigation = true;
  }
  if (isValidNavigation) {
    // While the forced password-change gate is active, the session-touch call would 403 (the
    // backend rejects authenticated calls until the password changes) and cascade into a logout —
    // skip it; the gate's only permitted authenticated call is changePassword itself.
    if (IsLoggedIn.value && to.name !== "Logout" && !sessionStore.IsMustChangePasswordActive) {
      await sessionStore.updateSessionLastAccessed();
    }
    await sessionStore.afterNavigateValidated();
    if (to.name !== "Logout") {
      await handleAppUpdates();
    }
    sessionStore.hideAppLoader();
  }
});

router.afterEach((to) => {
  const appSettingsStore = useAppSettingsStore();
  const { HideNavbar, ShowAppFooter, HideNavbarOnScroll } = storeToRefs(appSettingsStore);
  HideNavbar.value = !!to.meta.excludeNavbar;
  ShowAppFooter.value = !!to.meta.showAppFooter;
  HideNavbarOnScroll.value = false;
  const desiredStyle =
    (to.meta.statusBarStyle as StatusBarStyleEnum | undefined) ?? STATUS_BAR_DEFAULT_STYLE;
  if (desiredStyle !== appSettingsStore.StatusBarStyle) {
    appSettingsStore.setStatusBarStyle(desiredStyle);
  }
});

// A lazy ROUTE chunk that fails to load is the one dynamic-import failure worth a page reload: the
// navigation dies with nothing rendered, so there is no error state for the user to act on, and
// the usual cause is a session that outlived a deploy — which re-fetching index.html fixes.
// Feature-chunk failures deliberately never reach here. They reject inside the feature that
// awaited them and are reported (not reloaded) by the `vite:preloadError` listener in main.ts.
router.onError((error) => {
  if (isDynamicImportError(error)) {
    void useAppSettingsStore().handleRouteChunkLoadError(getFailedModuleUrl(error));
  }
});

export default router;
